import { Button, theme as AntTheme, Flex, Space, Typography } from 'antd';
import { CheckSquareOutlined, CloseSquareOutlined } from '@ant-design/icons';
import { CardTable } from '@copilot/common/components/tables/cardTable';
import { useTheme } from 'styled-components';
import { useState } from 'react';
import { Account } from './types';
import ActiveStatus from '@copilot/common/pages/accountDetailsPage/activeStatus';
import { UtilityFunctions } from '@copilot/common/utils/common';
import isUndefined from 'lodash/isUndefined';
import Actions, { Action } from '@copilot/common/actions';
import drawerManager from '@copilot/common/utils/drawerManager';
import { IAccountManager } from '@copilot/data/graphql/_generated';

type OnImpersonationCallback = (userId: string, orgId: string, path?: string) => void;

type OnActionCallback = (accountId: string, accountName: string) => void;

type ActionsCellProps = Readonly<{
	/** list of actions */
	subActions: Action[];

	/**
	 * Handler for the main action is triggered
	 * The main action is disabled if onAction is undefined
	 */
	onAction?: () => void;

	/**
	 * Main action label
	 */
	actionLabel: string;
}>;

/**
 * Component for actions in a table row
 * @param onAction main action
 * @param actionLabel action label
 * @param subActions sub actions
 * @constructor
 */
function ActionsCell({ onAction, actionLabel, subActions }: ActionsCellProps): JSX.Element {
	return (
		<Space.Compact style={{ alignItems: 'center' }} onClick={UtilityFunctions.stopPropogation}>
			<Button onClick={onAction} disabled={isUndefined(onAction)} type="primary" block>
				{actionLabel}
			</Button>
			<Actions actions={subActions} />
		</Space.Compact>
	);
}

/**
 * Props for LinkedInConnectionCell
 */
type LinkedInConnectionCellProps = Readonly<{
	/**
	 * Number of errors to display
	 */
	errorCount: number;
	/**
	 * Callback for clicking on cell contents
	 * If undefined link is disabled
	 */
	onClick?: OnImpersonationCallback;
	/**
	 * The user this cell is rendering for
	 */
	userId: string;
	/**
	 * The org this cell is rendering for
	 */
	orgId: string;
}>;

/**
 * Render component for the LinkedInConnection column
 */
function LinkedInConnectionCell({
	errorCount,
	onClick,
	userId,
	orgId,
}: LinkedInConnectionCellProps): JSX.Element {
	const { token } = AntTheme.useToken();
	if (isUndefined(onClick))
		return (
			<Typography.Text style={{ color: 'inherit' }}>
				<CloseSquareOutlined style={{ color: 'inherit' }} /> {errorCount}
			</Typography.Text>
		);
	if (errorCount <= 0) return <CheckSquareOutlined style={{ color: token.colorSuccess }} />;
	return (
		<Typography.Link
			onClick={(e) => {
				e.stopPropagation();
				onClick(userId, orgId, '/?tab=teammembers');
			}}
		>
			<CloseSquareOutlined style={{ color: token.colorError }} /> {errorCount}
		</Typography.Link>
	);
}

const ACTIONS_COLUMN_WIDTH = 220;
const STATS_COLUMN_WIDTH = 150;

type AccountTableProps = Readonly<{
	/**
	 * The accounts we want to show
	 */
	data: Account[];
	/**
	 * Whether to show the loading state
	 */
	loading?: boolean;
	/**
	 * Whether to show an account manager
	 */
	showAccountManager?: boolean;
	/**
	 * Handler for when impersonation is triggered
	 * @param userId The user we are impersonating
	 * @param orgId The org we are impersonating into
	 */
	onImpersonation: OnImpersonationCallback;
	/**
	 * Text to display when data is empty
	 */
	emptyText?: string;
	/**
	 * Called on selection of an account
	 * @param accountId Id of selected account
	 */
	onAccountSelected?: (accountId: string) => void;
	/**
	 * Callback to activate an account
	 * @param accountId Id of the selected account
	 */
	onAccountActivate?: OnActionCallback;
	/**
	 * Callback to deactivate an account
	 * @param accountId Id of the selected account
	 */
	onAccountDeactivate?: OnActionCallback;
	/**
	 * Callback to delete an account
	 * @param accountId Id of the selected account
	 */
	onAccountDelete?: OnActionCallback;
	/**
	 * List of account managers
	 */
	accountManagers?: Readonly<IAccountManager[]>;
}>;

type PaginationState = Readonly<{ page: number; pageSize: number }>;

/**
 * Table to show a list of accounts
 * @param props
 * @returns
 */
export default function AccountTable({
	data,
	accountManagers,
	onImpersonation,
	onAccountSelected,
	onAccountDeactivate,
	onAccountActivate,
	onAccountDelete,
	showAccountManager = false,
	loading = false,
	emptyText = '',
}: AccountTableProps) {
	const { token } = AntTheme.useToken();
	const [pageState, setPageState] = useState<PaginationState>({ page: 1, pageSize: 10 });
	const dataCount = data.length;
	const displayedIndexStart = (pageState.page - 1) * pageState.pageSize + 1;
	const displayedIndexEnd = Math.min(displayedIndexStart + pageState.pageSize - 1, data.length);
	const theme = useTheme();
	const rowConfig = (account: Account) => {
		if (isUndefined(onAccountSelected)) return {};
		return {
			onClick: () => {
				onAccountSelected(account.id);
			},
			style: {
				cursor: 'pointer',
				color: account.status !== 'Active' ? theme['@disabled-color'] : undefined,
			},
		};
	};

	function rowRender(_val: unknown, account: Account) {
		const subActions = [
			...(onAccountActivate && onAccountDeactivate
				? [
						{
							label:
								account.status === 'Active'
									? 'Deactivate account'
									: 'Activate account',
							onAction:
								account.status === 'Active'
									? () => onAccountDeactivate(account.id, account.name)
									: () => onAccountActivate(account.id, account.name),
						},
						{
							label: 'Edit account',
							onAction: () =>
								drawerManager.openAccountEditDrawer({
									account,
									accountManagers,
								}),
						},
				  ]
				: []),
			...(onAccountDelete
				? [
						{
							label: 'Delete account',
							onAction: () => onAccountDelete(account.id, account.name),
							isDanger: true,
						},
				  ]
				: []),
		];

		return (
			<ActionsCell
				actionLabel={'View All Campaigns'}
				onAction={
					account.status === 'Active'
						? () =>
								onImpersonation(
									account.primaryContact?.id ?? '',
									account.orgId,
									'/allcampaigns'
								)
						: undefined
				}
				subActions={subActions}
			/>
		);
	}

	return (
		<CardTable
			loading={loading}
			rowKey="id"
			headerBgColor={token.Layout?.bodyBg}
			dataSource={data}
			locale={{
				emptyText: (
					<Flex
						justify="space-around"
						align="center"
						style={{ height: 100, width: '100%' }}
					>
						{loading ? 'Loading....' : emptyText}
					</Flex>
				),
			}}
			pagination={{
				position: ['bottomLeft'],
				showSizeChanger: true,
				showQuickJumper: true,
				size: 'default',
				onChange: (page, pageSize) => {
					setPageState({ page, pageSize });
				},
				pageSize: pageState.pageSize,
				current: pageState.page,
			}}
			onRow={rowConfig}
		>
			<CardTable.Column
				title="Name"
				dataIndex="name"
				key="name"
				ellipsis
				fixed="left"
				render={(value) => (
					<Typography.Text ellipsis style={{ color: 'inherit' }}>
						{value}
					</Typography.Text>
				)}
			/>
			<CardTable.Column
				title="Active Users"
				dataIndex="activeUsers"
				key="activeUsers"
				width={STATS_COLUMN_WIDTH}
			/>
			<CardTable.Column
				title="Deactivated Users"
				dataIndex="inactiveUsers"
				key="inactiveUsers"
				width={STATS_COLUMN_WIDTH}
			/>
			<CardTable.Column<Account>
				title="LinkedIn Connection"
				dataIndex="linkedInConnection"
				key="linkedInConnection"
				width={STATS_COLUMN_WIDTH}
				render={(value, account) => (
					<LinkedInConnectionCell
						errorCount={value}
						onClick={account.status === 'Active' ? onImpersonation : undefined}
						userId={account.primaryContact?.id ?? ''}
						orgId={account.orgId}
					/>
				)}
			/>
			<CardTable.Column
				title="Status"
				dataIndex="status"
				key="status"
				width={STATS_COLUMN_WIDTH}
				render={(value) => <ActiveStatus status={value} />}
			/>
			{showAccountManager && (
				<CardTable.Column
					title="Acc. Manager"
					dataIndex="accountManager"
					key="accountManager"
					render={(value) =>
						Boolean(value) ? `${value.firstName} ${value.lastName}` : '-'
					}
					width={STATS_COLUMN_WIDTH}
				/>
			)}
			<CardTable.Column<Account>
				title={
					dataCount > 0 ? (
						<span style={{ fontWeight: 'normal' }}>
							{displayedIndexStart} - {displayedIndexEnd} of {dataCount}
						</span>
					) : null
				}
				key="col_actions"
				width={ACTIONS_COLUMN_WIDTH}
				align="right"
				fixed="right"
				render={rowRender}
			/>
		</CardTable>
	);
}
